/**
 * Frontend client for the Cloudflare-Worker AI endpoints. The Worker
 * proxies the Anthropic API so the API key never leaves the edge.
 *
 * Vite's dev server proxies /api/* to localhost:8787 (see vite.config.ts).
 * In production it goes through the same hostname (Pages + Worker on
 * separate URLs require a custom domain or VITE_API_BASE; see TODO below).
 */

import type { ChannelEvent, EventChannel } from './channel-events';

// VITE_API_BASE lets prod swap to a Worker URL (e.g. https://pulsewright-worker.x.workers.dev).
// Default '' = same origin; the Vite dev server proxies /api to localhost:8787.
const DEFAULT_API_BASE: string =
  (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_API_BASE ?? '';

export interface GenerationMeta {
  cached: boolean;
  tokens_in: number;
  tokens_out: number;
  model: string;
  template_version: string;
}

export interface CounterpointRequest {
  key: string;
  tempo: number;
  length: number;
  existing_channels: Record<string, ChannelEvent[]>;
  target_channel: EventChannel;
  instruction: string;
}

export interface CounterpointResult {
  channel: EventChannel;
  events: ChannelEvent[];
  meta: GenerationMeta;
}

export class AIClientError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'AIClientError';
    this.status = status;
    this.body = body;
  }
}

export async function generateCounterpoint(
  req: CounterpointRequest,
  apiBase: string = DEFAULT_API_BASE,
): Promise<CounterpointResult> {
  const response = await fetch(`${apiBase}/api/generate/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode: 'counterpoint', ...req }),
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      (body && typeof body === 'object' && 'error' in body && String(body.error)) ||
      `HTTP ${response.status}`;
    throw new AIClientError(message, response.status, body);
  }
  // The Worker returns { data: { channel, events }, meta }.
  const env = body as { data: { channel: EventChannel; events: ChannelEvent[] }; meta: GenerationMeta };
  return {
    channel: env.data.channel,
    events: env.data.events,
    meta: env.meta,
  };
}
